import type { z } from 'zod'
import { AUTH_DISABLED, supabase } from '@/auth/supabase'

/**
 * The single door to the backend.
 *
 * Two failure modes are kept distinct all the way to the UI, because collapsing
 * them produces a genuinely maddening experience:
 *   401 — not signed in, or the session expired  -> refresh, else /login
 *   403 — signed in, but no active subscription  -> /upgrade
 * Showing a login form to a subscriber whose card lapsed is the bug this
 * prevents.
 */

const BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

/** Signed in, but not entitled. Routed to /upgrade, never to /login. */
export class NotSubscribedError extends ApiError {
  constructor(message: string) {
    super(403, message)
    this.name = 'NotSubscribedError'
  }
}

/** No usable session. Routed to /login, preserving the attempted URL. */
export class UnauthenticatedError extends ApiError {
  constructor(message: string) {
    super(401, message)
    this.name = 'UnauthenticatedError'
  }
}

/** The API could not be reached at all: connection refused, or the dev proxy
 *  returning a gateway error because nothing is listening behind it. Worth its
 *  own type because "Bad Gateway" tells a developer nothing actionable. */
export class ApiUnreachableError extends ApiError {
  constructor(status: number, detail: string) {
    super(status, detail)
    this.name = 'ApiUnreachableError'
  }
}

const UNREACHABLE_HINT = import.meta.env.DEV
  ? 'The API is not responding. Is the backend running? Start it with: cd backend && python3 -m uvicorn app.main:app --port 8000'
  : 'The service is temporarily unavailable. Please try again in a moment.'

async function authHeader(): Promise<Record<string, string>> {
  if (AUTH_DISABLED || !supabase) return {}
  // Read the session per request rather than caching the token in a module
  // variable: a token refreshed in the background would otherwise be ignored
  // until a full page reload.
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>

function toQuery(params?: QueryParams) {
  if (!params) return ''
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

async function rawFetch(
  path: string,
  params?: QueryParams,
  retry = true,
): Promise<Response> {
  const res = await fetch(`${BASE}${path}${toQuery(params)}`, {
    headers: { Accept: 'application/json', ...(await authHeader()) },
  })

  if (res.status === 401 && retry && supabase && !AUTH_DISABLED) {
    // One silent refresh-and-retry, so a token expiring mid-session is
    // invisible to the user rather than an unexplained bounce to /login.
    const { data, error } = await supabase.auth.refreshSession()
    if (!error && data.session) return rawFetch(path, params, false)
  }
  return res
}

async function detail(res: Response): Promise<string> {
  try {
    const body = await res.json()
    if (typeof body?.detail === 'string') return body.detail
    if (Array.isArray(body?.detail))
      return body.detail.map((d: { msg?: string }) => d.msg).join('; ')
  } catch {
    /* fall through to the status text */
  }
  return res.statusText || `Request failed (${res.status})`
}

/** GET + parse. The schema is the contract; a drift is an error here, not a
 *  mystery `undefined` three components deep. */
export async function apiGet<S extends z.ZodType>(
  path: string,
  schema: S,
  params?: QueryParams,
): Promise<z.infer<S>> {
  const res = await rawFetch(path, params)

  if (res.status === 401) throw new UnauthenticatedError(await detail(res))
  if (res.status === 403) throw new NotSubscribedError(await detail(res))
  // 502/503/504 from the dev proxy mean nothing is listening behind it, which
  // is a different problem from the API returning an error.
  if ([502, 503, 504].includes(res.status)) {
    throw new ApiUnreachableError(res.status, UNREACHABLE_HINT)
  }
  if (!res.ok) throw new ApiError(res.status, await detail(res))

  const parsed = schema.safeParse(await res.json())
  if (!parsed.success) {
    console.error('API response failed validation', path, parsed.error.issues)
    throw new ApiError(500, 'The server returned data in an unexpected shape.')
  }
  return parsed.data
}
